import React, { useState, useRef } from 'react';
import { 
  Mail, Phone, MapPin, Clock, Send, 
  CheckCircle, AlertCircle, Loader2,
  Building, Shield, Users, FileText
} from 'lucide-react';

const App = () => {
  const [status, setStatus] = useState('IDLE');
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    asunto: '',
    mensaje: ''
  });
  
  const formRef = useRef(null);
  const controllerRef = useRef(null);

  // Cambia esta URL según tu entorno
  const BACKEND_URL = "https://siga.jalisco.gob.mx/backendDenuncias/api/contacto";

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Resetear errores
    setErrorMessage('');
    setValidationErrors([]);
    
    // Validación básica
    const errors = [];
    if (!formData.nombre.trim()) errors.push('nombre');
    if (!formData.email.trim()) errors.push('email');
    if (!formData.asunto.trim()) errors.push('asunto');
    if (!formData.mensaje.trim()) errors.push('mensaje');
    
    if (errors.length > 0) {
      setStatus('ERROR');
      setErrorMessage('Por favor, completa todos los campos obligatorios.');
      setValidationErrors(errors);
      return;
    }
    
    // Validación de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus('ERROR');
      setErrorMessage('Por favor, ingresa un correo electrónico válido.');
      setValidationErrors(['email']);
      return;
    }

    if (status === 'LOADING') return;

    // Cancelar petición anterior si existe
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    setStatus('LOADING');
    
    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      const response = await fetch(BACKEND_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nombre: formData.nombre.trim(),
          email: formData.email.trim(),
          asunto: formData.asunto.trim(),
          mensaje: formData.mensaje.trim(),
          fecha: new Date().toISOString(),
          origen: 'formulario_contacto_web'
        }),
        signal: controller.signal
      });

      // Intentar parsear la respuesta como JSON
      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error('Error parseando JSON:', parseError);
        throw new Error('El servidor devolvió una respuesta inválida');
      }

      if (!response.ok) {
        // Si el servidor devolvió errores de validación
        if (data.errors && Array.isArray(data.errors)) {
          setValidationErrors(data.errors.map(err => {
            if (err.includes('nombre')) return 'nombre';
            if (err.includes('correo') || err.includes('email')) return 'email';
            if (err.includes('asunto')) return 'asunto';
            if (err.includes('mensaje')) return 'mensaje';
            return 'general';
          }));
        }
        throw new Error(data.message || `Error del servidor (${response.status})`);
      }

      setStatus('SUCCESS');
      setFormData({ nombre: '', email: '', asunto: '', mensaje: '' });
      
      // Resetear formulario
      if (formRef.current) {
        formRef.current.reset();
      }
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Petición cancelada por el usuario');
        setStatus('IDLE');
      } else {
        console.error("Error en el envío:", error);
        setStatus('ERROR');
        setErrorMessage(error.message || 'Error al conectar con el servidor. Por favor, intenta nuevamente.');
      }
    } finally {
      controllerRef.current = null;
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    
    // Limpiar error del campo cuando el usuario escribe
    if (validationErrors.includes(id)) {
      setValidationErrors(prev => prev.filter(error => error !== id));
    }
    if (status === 'ERROR') {
      setStatus('IDLE');
      setErrorMessage('');
    }
  };

  const handleReset = () => {
    setStatus('IDLE');
    setErrorMessage('');
    setValidationErrors([]);
  };

  // Helper para verificar si un campo tiene error
  const hasError = (fieldName) => validationErrors.includes(fieldName);

  // Servicios
  const servicios = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Ordenamiento Territorial",
      description: "Planes y programas de ordenamiento ecológico"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Protección Ambiental",
      description: "Conservación de áreas naturales protegidas"
    },
    {
      icon: <Building className="w-6 h-6" />,
      title: "Desarrollo Sustentable",
      description: "Asesoría en proyectos con impacto ambiental"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Participación Ciudadana",
      description: "Programas de educación y conciencia ambiental"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white font-sans">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <div className="bg-emerald-100 p-2 rounded-lg">
                <div className="w-8 h-8 bg-emerald-600 rounded"></div>
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800">SEMADET</h1>
                <p className="text-xs text-slate-600">Secretaría de Medio Ambiente</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-emerald-700">Gobierno de Jalisco</p>
              <p className="text-xs text-slate-500">Dirección de Ordenamiento Ecológico</p>
            </div>
          </div>
        </div>
      </header>

      <main className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-400 rounded-2xl shadow-lg mb-6">
              <Mail className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
              Centro de <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">Contacto</span>
            </h1>
            <p className="mt-4 text-lg text-slate-600 max-w-3xl mx-auto">
              En la Dirección de Ordenamiento Ecológico estamos para atender tus consultas, 
              solicitudes y propuestas relacionadas con el medio ambiente en Jalisco.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Columna Izquierda */}
            <div className="lg:col-span-1 space-y-8">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-8">Información de Contacto</h2>
                
                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-emerald-50 text-emerald-700 rounded-xl">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Ubicación</p>
                      <p className="text-slate-600 text-sm leading-relaxed mt-2">
                        Av. Circunvalación Agustín Yañez No. 2343, 
                        Col. Moderna, C.P. 44190, 
                        Guadalajara, Jalisco
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-emerald-50 text-emerald-700 rounded-xl">
                      <Phone size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Teléfono</p>
                      <p className="text-slate-600">(33) 3030-8250</p>
                      <p className="text-slate-500 text-sm mt-1">Extensión: 51234</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-emerald-50 text-emerald-700 rounded-xl">
                      <Mail size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Correo</p>
                      <a 
                        href="mailto:ordenamientoecologico.semadet@jalisco.gob.mx" 
                        className="text-emerald-600 hover:text-emerald-700 text-sm break-all"
                      >
                        ordenamientoecologico.semadet@jalisco.gob.mx
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-emerald-50 text-emerald-700 rounded-xl">
                      <Clock size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Horario</p>
                      <p className="text-slate-600">Lunes a Viernes 9:00 a 17:00 hrs</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-xl p-8 text-white">
                <h3 className="text-xl font-bold mb-6">Nuestros Servicios</h3>
                <div className="space-y-4">
                  {servicios.map((servicio, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                      <div className="p-2 bg-emerald-500/20 rounded-lg">
                        <div className="text-emerald-300">{servicio.icon}</div>
                      </div>
                      <div>
                        <p className="font-semibold">{servicio.title}</p>
                        <p className="text-slate-300 text-sm">{servicio.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Columna Derecha - Formulario */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-8 py-6">
                  <h2 className="text-2xl font-bold text-white">Formulario de Contacto</h2>
                </div>

                <div className="p-8 md:p-10">
                  {status === 'SUCCESS' ? (
                    <div className="text-center py-12">
                      <div className="inline-flex items-center justify-center w-24 h-24 bg-emerald-50 text-emerald-600 rounded-full mb-8">
                        <CheckCircle size={48} />
                      </div>
                      <h3 className="text-3xl font-bold text-emerald-900 mb-4">
                        ¡Mensaje Enviado Exitosamente!
                      </h3>
                      <p className="text-slate-600 mb-8 max-w-md mx-auto">
                        Hemos recibido tu mensaje y nos pondremos en contacto contigo 
                        en un plazo máximo de 48 horas hábiles.
                      </p>
                      <button 
                        onClick={handleReset}
                        className="px-10 py-4 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white font-bold rounded-xl shadow-lg transition-all inline-flex items-center gap-3"
                      >
                        <Send size={20} />
                        Enviar otro mensaje
                      </button>
                    </div>
                  ) : (
                    <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label htmlFor="nombre" className="block text-sm font-semibold text-slate-700">
                            Nombre Completo *
                          </label>
                          <input 
                            type="text" 
                            id="nombre" 
                            required 
                            value={formData.nombre}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-xl border-2 ${hasError('nombre') ? 'border-red-500' : 'border-slate-200'} focus:border-emerald-500 outline-none bg-white transition-all`}
                            placeholder="Tu nombre completo"
                            disabled={status === 'LOADING'}
                          />
                          {hasError('nombre') && (
                            <p className="text-red-500 text-sm">Este campo es requerido</p>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <label htmlFor="email" className="block text-sm font-semibold text-slate-700">
                            Correo Electrónico *
                          </label>
                          <input 
                            type="email" 
                            id="email" 
                            required 
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-xl border-2 ${hasError('email') ? 'border-red-500' : 'border-slate-200'} focus:border-emerald-500 outline-none bg-white transition-all`}
                            placeholder="tu@email.com"
                            disabled={status === 'LOADING'}
                          />
                          {hasError('email') && (
                            <p className="text-red-500 text-sm">Ingresa un correo válido</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <label htmlFor="asunto" className="block text-sm font-semibold text-slate-700">
                          Asunto del Mensaje *
                        </label>
                        <input 
                          type="text" 
                          id="asunto" 
                          required 
                          value={formData.asunto}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-xl border-2 ${hasError('asunto') ? 'border-red-500' : 'border-slate-200'} focus:border-emerald-500 outline-none bg-white transition-all`}
                          placeholder="¿En qué podemos ayudarte?"
                          disabled={status === 'LOADING'}
                        />
                        {hasError('asunto') && (
                          <p className="text-red-500 text-sm">Este campo es requerido</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <label htmlFor="mensaje" className="block text-sm font-semibold text-slate-700">
                          Mensaje Detallado *
                        </label>
                        <textarea 
                          id="mensaje" 
                          required 
                          rows="6" 
                          value={formData.mensaje}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-xl border-2 ${hasError('mensaje') ? 'border-red-500' : 'border-slate-200'} focus:border-emerald-500 outline-none bg-white resize-none transition-all`}
                          placeholder="Describe detalladamente tu consulta..."
                          disabled={status === 'LOADING'}
                        />
                        {hasError('mensaje') && (
                          <p className="text-red-500 text-sm">Este campo es requerido</p>
                        )}
                      </div>

                      {status === 'ERROR' && errorMessage && (
                        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5">
                          <div className="flex items-start gap-4">
                            <AlertCircle className="w-6 h-6 text-red-600 mt-0.5" />
                            <div>
                              <p className="font-bold text-red-700 text-lg mb-2">
                                Error al enviar el mensaje
                              </p>
                              <p className="text-red-600">{errorMessage}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="pt-6 border-t border-slate-200">
                        <button 
                          type="submit" 
                          disabled={status === 'LOADING'}
                          className="w-full px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3"
                        >
                          {status === 'LOADING' ? (
                            <>
                              <Loader2 className="animate-spin" size={24} />
                              <span>Enviando...</span>
                            </>
                          ) : (
                            <>
                              <Send size={24} />
                              <span>Enviar Mensaje</span>
                            </>
                          )}
                        </button>
                        
                        <p className="text-xs text-slate-500 mt-4 text-center">
                          * Campos obligatorios. Tus datos están protegidos según la Ley de Protección de Datos.
                        </p>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-slate-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="text-center">
            <p className="text-emerald-400 font-bold mb-2">Gobierno de Jalisco</p>
            <p className="text-slate-400">© {new Date().getFullYear()} SEMADET - Todos los derechos reservados</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;